import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Avatar,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { FeatureGrid } from '../../components/marketing/FeatureGrid';
import type { Feature } from '../../components/marketing/FeatureGrid';
import { TestimonialCarousel } from '../../components/marketing/TestimonialCarousel';
import type { Testimonial } from '../../components/marketing/TestimonialCarousel';
import { PressLogos } from '../../components/marketing/PressLogos';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TextsmsIcon from '@mui/icons-material/Textsms';
import PaymentIcon from '@mui/icons-material/Payment';
import LoopIcon from '@mui/icons-material/Loop';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DevicesIcon from '@mui/icons-material/Devices';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import BarChartIcon from '@mui/icons-material/BarChart';

const features: Feature[] = [
  {
    icon: <CalendarMonthIcon />,
    title: 'Scheduling',
    description: "Don't worry about the back and forth. We can make plans and manage your calendar.",
  },
  {
    icon: <TextsmsIcon />,
    title: 'Text Messaging',
    description: 'Have a quick request? Text it in to us and get it done ASAP.',
  },
  {
    icon: <PaymentIcon />,
    title: 'Purchasing',
    description: 'Not only can we research products, we can purchase them for you too! (dedicated plans only)',
  },
  {
    icon: <LoopIcon />,
    title: 'Rollover Requests',
    description: "Unused requests from previous months rollover and accumulate as long as you're a member. (standard plans only)",
  },
  {
    icon: <AutorenewIcon />,
    title: 'Recurring Requests',
    description: 'You can set frequently requested requests to repeat so you can set it and forget it.',
  },
  {
    icon: <DevicesIcon />,
    title: 'Accessibility',
    description: 'You can submit requests by phone, web, text, email or through our android and iphone apps.',
  },
  {
    icon: <CardGiftcardIcon />,
    title: 'Referral Credits',
    description: 'Invite friends and family to join Fancy Hands and get free credits for every signup.',
  },
  {
    icon: <BarChartIcon />,
    title: 'Usage Stats',
    description: "Like data? See how much time and effort you've saved by having us work for you.",
  },
];

const sampleRequests = [
  {
    avatar: '/marketing/avatars/Av-2.png',
    benefit: 'Spend more time enjoying your vacations instead of planning them.',
    type: 'Manage Travel Itineraries',
    content:
      "I'll be in Philadelphia this weekend. Please find a car service from the airport to my hotel at the Lowes Center City.",
  },
  {
    avatar: '/marketing/avatars/Av-4.png',
    benefit: "Stop making phone calls that you don't want to make.",
    type: 'Returns, Exchanges, & Customer Service Calls',
    content:
      'My cable bill just went up 30 dollars! Can you call them up and get the bill back down?!',
  },
  {
    avatar: '/marketing/avatars/Av-6.png',
    benefit: 'Focus your efforts on more important things while we handle the rest.',
    type: 'Time Consuming Research',
    content:
      'Please send me five kid-friendly gluten-free recipes I can make for dinner this week (with a separate ingredient list). Thanks!',
  },
];

const testimonials: Testimonial[] = [
  {
    name: 'Alexis Ohanian',
    title: 'Founder of Reddit',
    content:
      "Fancy Hands allows me to hand off tasks like scheduling, calling, and research so I can focus on what's most important. As an entrepreneur, I swear by Fancy Hands. It's the best personal assistant service.",
    avatarUrl: '/marketing/testimonials/alexis-ohanian.png',
  },
  {
    name: 'Amanda Steinberg',
    title: 'Founder of DailyWorth',
    content:
      "I'm Fancy Hands' biggest fan. As a business owner and single mom, there's no time for appointment scheduling, research, or other home-related tasks. Before Fancy Hands, stuff just didn't get done. Now I use it almost daily and it saves me hours.",
    avatarUrl: '/marketing/testimonials/amanda-steinberg-avatar.png',
  },
  {
    name: 'Gabriel Weinberg',
    title: 'CEO & Founder, DuckDuckGo',
    content:
      'Fancy Hands has fundamentally changed the way I think about getting things done. In other words, they really changed my way of life.',
    avatarUrl: '/marketing/testimonials/gabriel-weinberg-avatar.png',
  },
  {
    name: 'Nick Loper',
    title: 'Entrepreneur',
    content:
      "Fancy Hands has done massive amounts of research for my latest book and I'll admit it feels pretty bad-ass to have an army of on-demand assistants ready to do my bidding.",
    avatarUrl: '/marketing/testimonials/nick-loper-avatar.png',
  },
  {
    name: 'David Kadavy',
    title: 'Designer / Author',
    content:
      'Fancy Hands has been a great way for me to not only train myself on delegating, but it has also saved me a ton of time making phone calls, booking appointments, and doing research.',
    avatarUrl: '/marketing/testimonials/david-kadavy-avatar.png',
  },
];

const heroImages = ['/headers/planner.svg', '/headers/plans.svg', '/headers/crowd.svg'];
const heroImage = heroImages[Math.floor(Math.random() * heroImages.length)];

export const HomePage = () => {
  const navigate = useNavigate();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: 'linear-gradient(135deg, #1C90D9 0%, #20C0DE 100%)',
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  mb: 2,
                }}
              >
                We get things done.
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 400, opacity: 0.95, mb: 3, lineHeight: 1.6 }}
              >
                Fancy Hands is a team of <strong>real, US-based personal assistants</strong> —
                actual humans, not AI — ready to start working with you today. Whether you need
                a <em>dedicated assistant</em> or you want to delegate the occasional task,
                we're here for you.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                <Chip
                  label="🇺🇸 US-Based"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
                />
                <Chip
                  label="100% Human"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
                />
                <Chip
                  label="No AI"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
                />
                <Chip
                  label="16+ Years in Business"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500 }}
                />

              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/plans')}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    fontWeight: 600,
                    px: 4,
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/dedicated-assistant')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Dedicated Assistants
                </Button>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                component="img"
                src={heroImage}
                alt="Free your time"
                sx={{ width: '100%', height: 'auto', maxHeight: 350 }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* What We Do Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box textAlign="center" mb={6}>
          <Typography variant="h3" fontWeight={700} gutterBottom>
            What do we do?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            We are the fastest, most affordable personal assistant service in the US. Real humans
            who understand context, use judgment, and actually get things done. No AI, no bots —
            just experienced professionals ready to help.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {sampleRequests.map((request, index) => (
            <Grid key={index} size={{ xs: 12, md: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="body1" fontWeight={500} gutterBottom>
                    {request.benefit}
                  </Typography>
                  <Typography variant="caption" color="primary.main" fontWeight={600}>
                    {request.type}
                  </Typography>
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      bgcolor: 'action.hover',
                      borderRadius: 2,
                      position: 'relative',
                    }}
                  >
                    <Avatar
                      src={request.avatar}
                      sx={{
                        position: 'absolute',
                        top: -16,
                        left: 16,
                        width: 48,
                        height: 48,
                        border: '3px solid',
                        borderColor: 'background.paper',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ mt: 3, fontStyle: 'italic', color: 'text.secondary' }}
                    >
                      "{request.content}"
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: 'action.hover', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight={700} textAlign="center" gutterBottom mb={6}>
            We've baked in plenty of features.
          </Typography>
          <FeatureGrid features={features} columns={4} />
        </Container>
      </Box>

      {/* Comparison Chart */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={700} textAlign="center" gutterBottom>
            Standard vs Dedicated
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}
          >
            Choose the plan that fits your needs. Both options give you access to real, US-based
            human assistants.
          </Typography>

          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '1rem' }}>Feature</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                    Standard Plans
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                    Dedicated Plans
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Real, US-based humans</TableCell>
                  <TableCell align="center">
                    <CheckIcon sx={{ color: 'success.main' }} />
                  </TableCell>
                  <TableCell align="center">
                    <CheckIcon sx={{ color: 'success.main' }} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Same assistant every time</TableCell>
                  <TableCell align="center">
                    <CloseIcon sx={{ color: 'action.disabled' }} />
                  </TableCell>
                  <TableCell align="center">
                    <CheckIcon sx={{ color: 'success.main' }} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Make purchases on your behalf</TableCell>
                  <TableCell align="center">
                    <CloseIcon sx={{ color: 'action.disabled' }} />
                  </TableCell>
                  <TableCell align="center">
                    <CheckIcon sx={{ color: 'success.main' }} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Unused requests roll over</TableCell>
                  <TableCell align="center">
                    <CheckIcon sx={{ color: 'success.main' }} />
                  </TableCell>
                  <TableCell align="center">
                    <CloseIcon sx={{ color: 'action.disabled' }} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Billing model</TableCell>
                  <TableCell align="center">Per request</TableCell>
                  <TableCell align="center">Per hour</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Sign NDA with your assistant</TableCell>
                  <TableCell align="center">
                    <CloseIcon sx={{ color: 'action.disabled' }} />
                  </TableCell>
                  <TableCell align="center">
                    <CheckIcon sx={{ color: 'success.main' }} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Calendar management</TableCell>
                  <TableCell align="center">
                    <CloseIcon sx={{ color: 'action.disabled' }} />
                  </TableCell>
                  <TableCell align="center">
                    <CheckIcon sx={{ color: 'success.main' }} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Weekly check-in calls</TableCell>
                  <TableCell align="center">
                    <CloseIcon sx={{ color: 'action.disabled' }} />
                  </TableCell>
                  <TableCell align="center">
                    <CheckIcon sx={{ color: 'success.main' }} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Best for</TableCell>
                  <TableCell align="center" sx={{ color: 'text.secondary' }}>
                    Occasional tasks
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'text.secondary' }}>
                    Ongoing support
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 4 }}>
            <Button variant="outlined" size="large" onClick={() => navigate('/personal-assistants')}>
              Learn About Standard Plans
            </Button>
            <Button variant="contained" size="large" onClick={() => navigate('/dedicated-assistant')}>
              Learn About Dedicated Plans
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight={700} textAlign="center" gutterBottom mb={6}>
            What our clients say
          </Typography>
          <TestimonialCarousel testimonials={testimonials} />
        </Container>
      </Box>

      {/* Plans CTA */}
      <Box sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/plans')}
            sx={{
              px: 6,
              py: 2,
              fontSize: '1.25rem',
              fontWeight: 600,
            }}
          >
            Plans and Pricing
          </Button>
        </Container>
      </Box>

      {/* Press Logos */}
      <PressLogos />
    </Box>
  );
};
